var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SnippetState_1;
/** @file This file contains the store for the current logged in user. */
import moment from 'moment';
import * as apiModels from '@codegrade/api';
import { Maybe, Nothing } from '@codegrade/maybe';
import { datetime, eitherAsync, immutable, object, promise, sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client, { Client, unauthorizedClient } from '@/client';
import { LoggedInUser } from '@/models';
import { getDefaultGlobalPermissions, GlobalPermissions as GPerm, } from '@/permissions';
import * as utils from '@/utils';
import { isNetworkError } from '@/utils/error';
const storeBuilder = getStoreBuilder();
/**
 * The state of the snippets of a user.
 */
let SnippetState = SnippetState_1 = class SnippetState {
    data;
    lastLoaded;
    /**
     * @param data A mapping between snippet key and the snippet.
     * @param lastLoaded Maybe the last time the snippets were loaded. If this
     * is `Nothing` the snippets have not been loaded yet.
     */
    constructor(data = {}, lastLoaded = Maybe.empty()) {
        this.data = data;
        this.lastLoaded = lastLoaded;
        Object.freeze(this.data);
    }
    /**
     * Add a new snippet to this state.
     *
     * @param snippet The snippet to add.
     * @returns A fresh snippet state.
     */
    addSnippet(snippet) {
        const data = { ...this.data };
        const oldSnippet = Object.values(data).find(s => s.id === snippet.id);
        if (oldSnippet != null) {
            utils.vueDelete(data, oldSnippet.key);
        }
        return new SnippetState_1({ ...data, [snippet.key]: snippet }, this.lastLoaded);
    }
    /**
     * Remove a snippet from this state.
     *
     * @param toRemove The snippet to remove.
     * @returns A fresh snippet state without the given snippet.
     */
    deleteSnippet(toRemove) {
        return new SnippetState_1(object.filterObject(this.data, snippet => snippet.key !== toRemove.key), this.lastLoaded);
    }
};
SnippetState = SnippetState_1 = __decorate([
    immutable.immutable
], SnippetState);
/**
 * Create the initial state for the `UserStore`.
 *
 * @returns A fresh initial state.
 */
const makeInitialState = () => ({
    loggedInUser: Nothing,
    snippets: Nothing,
    permissions: {},
    permissionsLoaders: {},
});
const moduleBuilder = storeBuilder.module('user', makeInitialState());
const manageSitePermissions = Object.freeze(Object.values(GPerm).filter(perm => perm.manageAdmin));
/**
 * Check if two restrictions are the same.
 *
 * @param a The first restriction to test.
 * @param b The second restriction to test.
 * @returns If they are the same.
 */
function restrictionsEq(a, b) {
    if (a.forContext?.courseId !== b.forContext?.courseId) {
        return false;
    }
    return utils.setEq(a.removedPermissions?.course ?? new Set(), b.removedPermissions?.course ?? new Set());
}
/**
 * Check if two users are the same.
 *
 * @param a The first user to test.
 * @param b The second user to test.
 * @returns If they are the same.
 */
function userEq(a, b) {
    return a.id === b.id && restrictionsEq(a.session.restrictions, b.session.restrictions);
}
/**
 * Create a new logged in user.
 *
 * @param root0 The data to create the user with.
 * @returns The fresh logged in user.
 */
function createLoggedInUser({ user: userData, sessions, loginMethod, }) {
    return new LoggedInUser(userData, {
        canSeeHidden: userData.hidden,
        permissions: userData.permissions,
        sessions,
        loginMethod,
        email: userData.email,
        fullyLoaded: true,
    });
}
/** The User store module. */
export var UserStore;
(function (UserStore) {
    /**
     * A getter to check if we have a logged in user.
     */
    UserStore.loggedIn = moduleBuilder.read(state => state.loggedInUser.isJust(), 'loggedIn');
    /**
     * A getter to retrieve the user id of the currently logged in user. Returns
     * 0 if there is no logged in user.
     */
    UserStore.id = moduleBuilder.read(state => state.loggedInUser.mapOrDefault(u => u.id, 0), 'id');
    /**
     * A getter for the snippets of the currently logged in user.
     */
    UserStore.snippets = moduleBuilder.read(state => state.snippets.mapOrDefault(s => s.data, {}), 'snippets');
    /**
     * Get the currently logged in user.
     *
     * @param state The state of the user store.
     * @returns The currenlty logged in user if it exists and the user can be found.
     */
    function getUser(state) {
        return state.loggedInUser;
    }
    /**
     * The currently logged in user as a `models.LoggedInUser` object.
     */
    UserStore.loggedInUser = moduleBuilder.read((state) => state.loggedInUser, 'loggedInUser');
    /**
     * A getter for the currently logged in user.
     *
     * @deprecated
     */
    UserStore.currentUser = moduleBuilder.read(getUser, 'currentUser');
    /**
     * A getter to retrieve maybe the tenant id of the currently logged in user.
     */
    UserStore.tenantId = moduleBuilder.read(() => UserStore.loggedInUser().map(u => u.tenantId), 'tenantId');
    /**
     * Retrieve an attribute of the current user.
     *
     * @param attr The attribute to retireve of the current user.
     * @returns A function to retrieve the give attribute of the current user,
     * or '' if there is no current user at the time of calling the returned
     * function.
     */
    function userAttr(attr) {
        return (state) => state.loggedInUser.mapOrDefault(u => u[attr], '');
    }
    /** Getter of username. */
    UserStore.username = moduleBuilder.read(userAttr('username'), 'username');
    /** Getter of `canSeeHidden` of the user. */
    UserStore.canSeeHidden = moduleBuilder.read(state => state.loggedInUser.mapOrDefault(u => u.canSeeHidden, false), 'canSeeHidden');
    const getGlobalPermissions = moduleBuilder.read(state => state.loggedInUser.map(u => u.permissions).orDefaultLazy(getDefaultGlobalPermissions), 'getGlobalPermissions');
    /**
     * Get the permission mapping for the current user in the given tenant.
     *
     * When the value of a certain permission in this mapping is `false` that does NOT mean
     * that the user does not have the permission, they might have the global permission!
     */
    const getTenantPermissions = moduleBuilder.read(state => 
    /**
     * Get the permission map for the given tenant.
     *
     * @param tid The tenant id to retrieve permissions for.
     * @returns The permission map for the tenant, or Nothing if not loaded.
     */
    (tid) => Maybe.fromNullable(state.permissions[tid]).join(), 'getTenantPermissions');
    /** Returns function to check if a user has a specific global permission. */
    UserStore.hasGlobalPermission = moduleBuilder.read(() => {
        const perms = getGlobalPermissions();
        const user = UserStore.loggedInUser();
        return (perm) => {
            const removed = user.extract()?.session.restrictions.removedPermissions?.global;
            if (removed != null && removed.has(perm.value)) {
                return false;
            }
            return perms[perm.value] ?? false;
        };
    }, 'hasGlobalPermission');
    /** Returns function that checks if a user has a tenant permission. */
    UserStore.hasTenantPermission = moduleBuilder.read(() => {
        const getTenantPerms = getTenantPermissions();
        const hasGlobalPerm = UserStore.hasGlobalPermission();
        /**
         * Check if a user has a global permission in a tenant.
         *
         * @param perm The permission to check for.
         * @param tid The tenant id to check for the permission in.
         * @returns If the current user has the given permission in the given tenant.
         */
        return (perm, tid) => getTenantPerms(tid)
            .chain(permMap => Maybe.fromFalsy(permMap[perm.value]))
            .orDefaultLazy(() => hasGlobalPerm(perm));
    }, 'hasTenantPermission');
    /** Returns function that check the user is an admin in the specified tenant.  */
    UserStore.isAdminIn = moduleBuilder.read(() => {
        const hasTenantPerm = UserStore.hasTenantPermission();
        return (tid) => hasTenantPerm(GPerm.is_admin, tid);
    }, 'isAdminIn');
    /**
     * Check whether the user can manage site via permission map.
     *
     * @param perms Global permission map.
     * @returns Whether the user has some manage site permissions.
     */
    function hasManagePerm(perms) {
        return manageSitePermissions.some(perm => perms[perm.value]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.canManageSite = moduleBuilder.read(() => hasManagePerm(getGlobalPermissions()), 'canManageSite');
    /** Store getter to get a function returning a boolean based on tenant management permission. */
    UserStore.canManageTenant = moduleBuilder.read(() => 
    /**
     * Function to get whether the user can manage tenant.
     *
     * @param tid The tenant id.
     * @returns If the user can manage tenant.
     */
    (tid) => getTenantPermissions()(tid)
        .chain(perms => Maybe.fromFalsy(hasManagePerm(perms)))
        .orDefaultLazy(UserStore.canManageSite), 'canManageTenant');
    /** Getter for a function that retrieves snippets by their id. */
    UserStore.findSnippetsByPrefix = moduleBuilder.read(state => {
        const values = state.snippets.mapOrDefault(({ data }) => sort.sortBy(Object.values(data), snip => [snip.key]), []);
        return (prefix) => values.filter(({ key }) => key.startsWith(prefix));
    }, 'findSnippetsByPrefix');
    /**
     * The current session of the user as login response.
     *
     * Only use this when you know what you are doing, as you might accidentally leak the session of
     * the user this way.
     */
    UserStore.dangerousSession = moduleBuilder.read((state) => state.loggedInUser
        .filter(user => {
        const expiresAt = user.session.expiresAt;
        if (expiresAt == null) {
            return true;
        }
        return expiresAt.isAfter(moment.utc().subtract(1, 'minute'));
    })
        .map((user) => ({
        user: user.toAPIv2(),
        accessToken: user.session.token,
        expiresAt: user.session.expiresAt,
        restrictions: user.session.restrictions,
    })), 'dangerousSession');
    const setSnippets = moduleBuilder.commit((state, newSnippets) => {
        utils.vueSet(state, 'snippets', Maybe.of(new SnippetState(newSnippets, Maybe.of(new datetime.RelativeTime()))));
    }, 'setSnippets');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.addSnippet = moduleBuilder.commit((state, payload) => {
        const curSnippets = state.snippets.orDefaultLazy(() => new SnippetState());
        utils.vueSet(state, 'snippets', Maybe.of(curSnippets.addSnippet(payload)));
    }, 'addSnippet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.updateSnippet = moduleBuilder.commit((_, payload) => UserStore.addSnippet(payload), 'updateSnippet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.deleteSnippet = moduleBuilder.commit((state, payload) => {
        utils.vueSet(state, 'snippets', state.snippets.map(s => s.deleteSnippet(payload)));
    }, 'deleteSnippet');
    const commitLogout = moduleBuilder.commit(state => {
        Object.entries(makeInitialState()).forEach(([key, value]) => {
            utils.vueSet(state, key, value);
        });
    }, 'commitLogout');
    /** Login a user. */
    const commitLogin = moduleBuilder.commit((state, data) => {
        utils.vueSet(state, 'loggedInUser', Maybe.of(data.user));
    }, 'commitLogin');
    const commitTenantPermissions = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => utils.vueSet(state.permissions, payload.tenantId, payload.perms), 'commitTenantPermissions');
    /** Set a permission for a user. */
    UserStore.setPermission = moduleBuilder.commit((state, payload) => {
        const { permission, value } = payload;
        payload.tenantId.caseOf({
            Nothing: () => {
                UserStore.loggedInUser().ifJust(u => commitLogin(u.setPermission(permission, value)));
            },
            Just: tId => {
                Maybe.fromNullable(state.permissions[tId])
                    .join()
                    .ifJust(perms => {
                    const newPerms = Object.freeze({ ...perms, [permission]: value });
                    utils.vueSet(state.permissions, tId, Maybe.of(newPerms));
                });
            },
        });
    }, 'setPermission');
    /** Logout the currently logged in user. */
    UserStore.logout = moduleBuilder.dispatch(async (_, args) => {
        const { signals = true } = args;
        const cur = UserStore.loggedInUser().extract();
        if (cur != null && args.revoke) {
            const confirm = args.confirmSessionTermination ? 'true' : undefined;
            await Promise.all(cur.sessions.map(({ token }) => unauthorizedClient.p.user.logout({
                requestBody: {
                    token,
                    confirmSessionTermination: confirm,
                },
            })));
        }
        commitLogout();
        if (signals) {
            await utils.signal.AFTER_USER_CHANGED.sendPromise(Maybe.empty());
        }
    }, 'logout');
    /** Do a login using the username, tenant id, and password of a user. */
    UserStore.loginWithUsername = moduleBuilder.dispatch(async (ctx, { data, loginMethod, }) => {
        const response = await client.p.user.login({
            requestBody: data,
        });
        return response.addCallbacks({
            onSuccess: async ({ cgResult }) => {
                const wasLoggedIn = UserStore.loggedIn();
                if (wasLoggedIn) {
                    await UserStore.logout({
                        revoke: true,
                        signals: false,
                        confirmSessionTermination: data.confirmSessionTermination ?? false,
                    });
                }
                const user = createLoggedInUser({
                    user: cgResult.user,
                    sessions: [
                        {
                            restrictions: cgResult.restrictions,
                            token: cgResult.accessToken,
                            expiresAt: cgResult.expiresAt,
                        },
                    ],
                    loginMethod: Maybe.of(loginMethod),
                });
                const userChanged = ctx.state.loggedInUser.mapOrDefault(cur => !userEq(cur, user), true);
                commitLogin({ user });
                if (userChanged) {
                    await utils.signal.AFTER_USER_CHANGED.sendPromise(Maybe.of(user));
                }
            },
        });
    }, 'loginWithUsername');
    /** Returns function that refresh snippets. */
    UserStore.refreshSnippets = moduleBuilder.dispatch(async () => {
        const updatedSnippets = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.snippet.getAll({ nextToken: nextToken.extract() })));
        const result = Object.fromEntries(updatedSnippets.map(snip => [snip.key, snip]));
        setSnippets(result);
    }, 'refreshSnippets');
    /**
     * Conditionally refresh snippets, when it's been at least 5 minutes since last load.
     * One extra condition is added based on randomness to prevent excessive fetching.
     */
    UserStore.maybeRefreshSnippets = moduleBuilder.dispatch(async (ctx) => {
        if (ctx.state.snippets
            .chain(s => s.lastLoaded)
            .map(lastLoaded => lastLoaded.since().asMinutes())
            .map(diff => diff > 5 && Math.random() < ((diff - 5) / (60 * 24)) ** 2)
            .orDefault(true)) {
            await UserStore.refreshSnippets();
        }
    }, 'maybeRefreshSnippets');
    /**
     * Check that the given session is valid.
     *
     * @param session The session to check for validity.
     * @param maxTries The maximum amount of times to retry transient errors.
     * @returns A Promise that will resolve to the logged in user if the session is valid.
     */
    async function verifyLoginCheck({ session }, maxTries) {
        return promise.retry(async () => {
            const tmpClient = new Client({ token: Maybe.of(session.token) });
            const { body } = await tmpClient.p.user.get();
            return body;
        }, {
            shouldRetry: (tryN, err) => tryN < maxTries &&
                (isNetworkError(err) ||
                    (err instanceof apiModels.ApiResultError &&
                        Math.floor(err.response.status / 100) === 5)),
        });
    }
    /**
     * A getter to retrieve the current state of the student view.
     *
     * If the user is not logged in, the state is 'disabled'.
     * If the user has no removed course permissions, the state is 'disabled'.
     * Otherwise, the state is 'enabled' and indicates whether the user can leave the session.
     * We can only leave if we have a base session token to fall back to.
     *
     * @returns An object representing the state of the student view.
     */
    UserStore.studentView = moduleBuilder.read(() => {
        const user = UserStore.loggedInUser().extract();
        if (user == null) {
            return Object.freeze({ tag: 'disabled' });
        }
        return user.getStudentViewState();
    }, 'studentView');
    /** @returns The current exam session state. */
    UserStore.examSession = moduleBuilder.read(() => {
        const user = UserStore.loggedInUser().extract();
        if (user == null) {
            return Object.freeze({ tag: 'disabled' });
        }
        return user.getExamSessionState();
    }, 'examSession');
    /**
     * This replaces the current sessions at the top of the session stack.
     *
     * We use this in the context of the AssignmentAccessWrapper when an
     * assignment has been locked by password. When verifying a password, the
     * verification route will return an update session that tracks if the user
     * is allowed to view the assignment.
     */
    UserStore.replaceSession = moduleBuilder.dispatch(
    /**
     * The function to perform this action.
     *
     * @param _ The context, unused.
     * @param sessionData The new session data to apply.
     * @returns A promise that resolves once the user has been updated.
     */
    async (_, sessionData) => {
        const theCurrentUser = UserStore.loggedInUser().unsafeCoerce();
        const newSession = {
            token: sessionData.accessToken,
            restrictions: sessionData.restrictions,
            expiresAt: sessionData.expiresAt,
        };
        const newUser = theCurrentUser.replaceCurrentSession(newSession);
        commitLogin({ user: newUser });
    }, 'replaceSession');
    /** Verify that the login is working. */
    UserStore.verifyLogin = moduleBuilder.dispatch(
    /**
     * The function to perform this action.
     *
     * @param ctx The context to perform the action with.
     * @returns A promise that will resolve if the login is correct.
     */
    async (ctx) => await ctx.state.loggedInUser.caseOf({
        Nothing: () => Promise.reject(Error('Not logged in')),
        Just: async (curLoggedIn) => {
            const user = createLoggedInUser({
                user: await verifyLoginCheck(curLoggedIn, 10),
                loginMethod: curLoggedIn.loginMethod,
                sessions: curLoggedIn.sessions,
            });
            const userTheSame = ctx.state.loggedInUser.mapOrDefault(cur => userEq(cur, user), false);
            if (userTheSame) {
                commitLogin({ user });
            }
            return user;
        },
    }), 'verifyLogin');
    /**
     * Action to update basic user information.
     */
    UserStore.updateUserInfo = moduleBuilder.dispatch(async (ctx, data) => {
        const curUser = ctx.state.loggedInUser.unsafeCoerce();
        const response = await client.p.user.patch({
            userId: curUser.id,
            requestBody: {
                name: data.name,
                email: data.email,
                oldPassword: data.oldPw,
                newPassword: data.newPw,
            },
        });
        const user = curUser.updateFromApi(response.body);
        commitLogin({ user });
        return response;
    }, 'updateUserInfo');
    /** Update the access token and user in the store. */
    UserStore.updateAccessToken = moduleBuilder.dispatch(async (_, { loginResponse, loginMethod, revokeOldToken, confirmSessionTermination = false, }) => {
        await UserStore.logout(revokeOldToken
            ? {
                revoke: true,
                signals: false,
                confirmSessionTermination: confirmSessionTermination ?? false,
            }
            : { revoke: false, signals: false });
        const user = createLoggedInUser({
            user: loginResponse.user,
            sessions: [
                {
                    expiresAt: loginResponse.expiresAt,
                    restrictions: loginResponse.restrictions,
                    token: loginResponse.accessToken,
                },
            ],
            loginMethod: Maybe.of(loginMethod),
        });
        commitLogin({ user });
        await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(user)));
    }, 'updateAccessToken');
    /** Add restrictions to the currently logged in user. */
    UserStore.addRestrictions = moduleBuilder.dispatch(
    /**
     * Do the action to add restrictions.
     *
     * @param _ The context, used for dispatching.
     * @param data The restrictions to add, the current restrictions will not be removed by
     * passing an empty object.
     * @returns A promise that resolves once the restrictions have been added.
     */
    async (_, data) => {
        const user = UserStore.loggedInUser().unsafeCoerce();
        const { cgResult } = await client.p.user.restrict({ requestBody: data.restrictions });
        const newUser = user.addSession({
            restrictions: cgResult.restrictions,
            token: cgResult.accessToken,
            expiresAt: cgResult.expiresAt,
        });
        commitLogin({ user: newUser });
        await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(newUser)));
    }, 'addRestrictions');
    /** Remove the last restriction layer. */
    UserStore.popRestrictions = moduleBuilder.dispatch(
    /**
     * Do the action to pop a session.
     */
    async () => {
        const { oldSession, newUser } = UserStore.loggedInUser()
            .unsafeCoerce()
            .popSession()
            .unsafeCoerce();
        commitLogin({ user: newUser });
        await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(newUser)));
        await unauthorizedClient.p.user.logout({ requestBody: { token: oldSession.token } });
    }, 'popRestrictions');
    /** Load permission of the user on a tenant. */
    UserStore.loadPermissions = moduleBuilder.dispatch(async ({ state }, payload) => {
        const c = payload.client ?? client;
        return payload.tenantId.caseOf({
            Nothing: () => Promise.resolve(apiModels.ApiResult.fromResponseAndValue(new Response(), Maybe.of(getGlobalPermissions()))),
            Just: (tId) => utils.loadApiWithMaybeForce({
                value: Maybe.fromNullable(state.permissions[tId]),
                loader: Maybe.fromNullable(state.permissionsLoaders[tId]),
                force: false,
                load: async () => {
                    const response = await c.p.tenant.getPermissions({ tenantId: tId });
                    const perms = response.cgResult.hasPerms
                        ? Maybe.of(response.cgResult.perms)
                        : Maybe.empty();
                    commitTenantPermissions({
                        tenantId: tId,
                        perms,
                    });
                    return response.map(() => perms);
                },
                addLoader: loader => {
                    utils.vueSet(state.permissionsLoaders, tId, loader);
                },
                removeLoader: () => utils.vueDelete(state.permissionsLoaders, tId),
            }),
        });
    }, 'loadPermissions');
    utils.signal.AFTER_USER_CHANGED.register(async (user) => {
        if (user.isJust()) {
            await UserStore.loadPermissions({ tenantId: Maybe.of(user.value.tenantId) });
        }
    });
})(UserStore || (UserStore = {}));
